import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Loader2, AlertCircle } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

/**
 * TripSearch — the /trip landing page inside Classify.
 *
 * Ported from the standalone TRIP App.jsx:
 *   - Uses Classify's authenticated `api` helper (token auto-injected by the
 *     request interceptor) instead of raw fetch().
 *   - api baseURL already includes /api, so paths are '/trip/...'.
 *   - TRIP scoring takes 30-50s — the default api timeout is 30s, so the
 *     /score call overrides timeout to 90s. Otherwise it would time out.
 *   - React Router navigation to /trip/report/:id.
 *   - Demo tiles removed (production view).
 */
export default function TripSearch() {
  const navigate = useNavigate();

  const [businessName, setBusinessName] = useState('');
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [error, setError] = useState(null);
  const [recentReports, setRecentReports] = useState([]);

  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Load recent TRIP reports (tenant-scoped on the backend)
  useEffect(() => {
    async function loadRecent() {
      try {
        const res = await api.get('/trip/reports', { params: { limit: 10 } });
        setRecentReports(res.data.reports || []);
      } catch (err) {
        console.error('TRIP recent load error:', err);
      }
    }
    loadRecent();
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    function onClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Debounced autocomplete
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.trim().length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoadingSuggest(true);
      try {
        const res = await api.get('/trip/autocomplete', { params: { q: query } });
        setSuggestions(res.data.suggestions || []);
        setShowSuggestions(true);
        setActiveIdx(-1);
      } catch (err) {
        console.error('TRIP autocomplete error:', err);
        setSuggestions([]);
      } finally {
        setLoadingSuggest(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  async function selectSuggestion(s) {
    if (!s || scoring) return;
    setShowSuggestions(false);
    setError(null);
    setScoring(true);
    const toastId = toast.loading('Computing TRIP profile… (~30-50s)');

    try {
      // Resolve place details (lat/lng/components)
      const det = await api.get('/trip/place-details', { params: { place_id: s.place_id } });
      const details = det.data;

      // Run the score — override timeout (default api timeout is 30s; TRIP takes 30-50s)
      const res = await api.post('/trip/score', {
        lat: details.lat,
        lng: details.lng,
        address: details.address,
        city: details.components?.city || '',
        state: details.components?.state || '',
        business_name: businessName || null,
        lob: 'hotel'
      }, { timeout: 90000 });

      toast.dismiss(toastId);
      const reportId = res.data.report_id;
      if (!reportId) {
        toast.error('Report computed but could not be saved. Please retry.');
        setScoring(false);
        return;
      }
      toast.success('TRIP profile ready');
      navigate(`/trip/report/${reportId}`);
    } catch (err) {
      toast.dismiss(toastId);
      const msg = err.response?.data?.error || err.response?.data?.message || 'TRIP scoring failed';
      setError(msg);
      toast.error(msg);
      setScoring(false);
    }
  }

  function handleKeyDown(e) {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIdx >= 0) selectSuggestion(suggestions[activeIdx]);
      else if (suggestions[0]) selectSuggestion(suggestions[0]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  }

  if (scoring) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <Loader2 className="animate-spin text-teal mx-auto mb-6" size={48} />
        <div className="font-heading text-xl text-navy mb-2">Computing TRIP profile…</div>
        <div className="text-sm text-gray-500 max-w-md mx-auto">
          Running data sources in parallel — crime, 911, geographic, facilities,
          public records search, and narrative generation. Typically 30–50 seconds.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" ref={containerRef}>
      <div className="mb-8">
        <h1 className="font-heading text-2xl text-navy">Trafficking Risk Intelligence Profile</h1>
        <p className="text-gray-500 mt-1">
          Search any U.S. address to compute a location-based trafficking risk profile.
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm">
            <div className="font-semibold text-red-700 mb-1">TRIP request failed</div>
            <div className="text-red-600">{error}</div>
          </div>
        </div>
      )}

      <div className="card mb-8">
        {/* Optional business name */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Business name <span className="text-gray-300 font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={businessName}
            onChange={e => setBusinessName(e.target.value)}
            disabled={scoring}
            placeholder="e.g. United Inn & Suites"
            className="input-field"
          />
        </div>

        {/* Address autocomplete */}
        <div className="relative">
          <label className="block text-sm font-semibold text-gray-700 mb-1">Property address</label>
          <div className="relative">
            <Search size={18} className="absolute left-3.5 top-3 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              disabled={scoring}
              placeholder="Start typing an address…"
              className="input-field pl-11"
              autoComplete="off"
            />
            {loadingSuggest && (
              <Loader2 size={16} className="absolute right-3.5 top-3 text-teal animate-spin" />
            )}
          </div>

          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
              {suggestions.map((s, i) => (
                <button
                  key={s.place_id}
                  type="button"
                  onClick={() => selectSuggestion(s)}
                  onMouseEnter={() => setActiveIdx(i)}
                  className={`w-full text-left px-4 py-2.5 text-sm border-b last:border-b-0 border-gray-100 transition flex items-start gap-2.5 ${
                    activeIdx === i ? 'bg-teal-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <MapPin size={14} className="text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-navy truncate">{s.main_text}</div>
                    <div className="text-xs text-gray-500 truncate">{s.secondary_text}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent TRIP reports */}
      {recentReports.length > 0 && (
        <div>
          <div className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">
            Recent TRIP reports
          </div>
          <div className="space-y-1">
            {recentReports.slice(0, 8).map(r => (
              <button
                key={r._id}
                onClick={() => navigate(`/trip/report/${r._id}`)}
                className="w-full text-left px-4 py-2 rounded hover:bg-white border border-transparent hover:border-gray-200 text-sm flex items-center justify-between transition"
              >
                <div className="min-w-0">
                  <span className="font-semibold text-navy">{r.input?.business_name || 'Unnamed'}</span>
                  <span className="text-gray-500 ml-2">— {r.input?.address}</span>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-xs text-gray-400">{r.tier_label}</span>
                  <span className="font-bold text-lg font-heading" style={{ color: r.tier_color }}>
                    {r.score}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
